import { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Mail, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import axios from "axios";

/**
 * Public account deletion request page — required for Google Play compliance.
 * No auth needed. Submissions land in `deletion_requests`, trigger a
 * confirmation email to the requester and an alert to the Kobolog team.
 * Manual verification + delete happens within 30 days (industry standard).
 */
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DeleteAccountPublic() {
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setErr("Please enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/public/data-deletion-request`, {
        email: email.trim().toLowerCase(),
        reason: reason.trim(),
      });
      setOk(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-kudi-cream" data-testid="delete-account-public-page">
      <div className="max-w-lg mx-auto px-5 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-kudi-ink/70 hover:text-kudi-green mb-6"
          data-testid="delete-account-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Kobolog
        </Link>

        <div className="bg-white border border-border rounded-2xl p-6 sm:p-8">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4">
            <Trash2 className="w-6 h-6" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
            Request account & data deletion
          </h1>
          <p className="mt-3 text-sm text-kudi-ink/70 leading-relaxed">
            Kobolog AI is committed to your data rights. Submit this form and we'll delete your account and all
            associated data (transactions, inventory, debtors, chat history, reminders, notifications, wallet ledger)
            within 30 days. You'll receive an email confirmation when it's done.
          </p>

          {ok ? (
            <div className="mt-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3" data-testid="delete-request-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-800">Request received</div>
                <div className="text-sm text-emerald-700/85 mt-1">
                  We've emailed <span className="font-medium">{email}</span> to confirm your request.
                  Deletion will be completed within 30 days. If you change your mind, just reply to that email.
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="delete-request-form">
              <div>
                <label htmlFor="del-email" className="text-xs uppercase tracking-wider text-kudi-ink/60 font-semibold block mb-1.5">
                  Account email
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-kudi-ink/40 pointer-events-none" />
                  <input
                    id="del-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full pl-10 pr-3 py-2.5 border border-border rounded-xl text-sm focus:border-kudi-green focus:outline-none focus:ring-2 focus:ring-kudi-green/30"
                    data-testid="delete-request-email"
                    required
                  />
                </div>
                <div className="text-[11px] text-kudi-ink/50 mt-1">
                  Use the exact email you signed up with. We can't process requests for accounts we can't find.
                </div>
              </div>

              <div>
                <label htmlFor="del-reason" className="text-xs uppercase tracking-wider text-kudi-ink/60 font-semibold block mb-1.5">
                  Reason (optional)
                </label>
                <textarea
                  id="del-reason"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  maxLength={1000}
                  placeholder="Tell us why (this helps us improve)."
                  className="w-full px-3 py-2.5 border border-border rounded-xl text-sm focus:border-kudi-green focus:outline-none focus:ring-2 focus:ring-kudi-green/30 resize-none"
                  data-testid="delete-request-reason"
                />
              </div>

              {err && (
                <div className="rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700 px-3 py-2" data-testid="delete-request-error">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 bg-rose-600 text-white rounded-xl py-3 font-semibold text-sm hover:bg-rose-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="delete-request-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {busy ? "Submitting…" : "Submit deletion request"}
              </button>
              <p className="text-[11px] text-kudi-ink/45 text-center">
                Prefer to delete instantly? Log in and use the <span className="font-semibold">Delete my account</span> button in your Profile.
              </p>
            </form>
          )}
        </div>

        <div className="text-center text-[11px] text-kudi-ink/45 mt-6">
          Questions about your data? Email <a href="mailto:support@kobolog.ai" className="underline">support@kobolog.ai</a>.
        </div>
      </div>
    </div>
  );
}
